import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { ArrowLeft, Play, Edit, MoreVertical, ImageOff, BedDouble, Bath, Ruler, Layers, Download, Copy, Image as ImageIcon, Film, Globe, Loader2, Clock, Wand2, Sofa } from 'lucide-react';
import { mockSpecs, stagingStyles, type RenderStatus } from '../data/specsData';
import { initialModels } from '../data/optionsData';
export function SpecDetailPage() {
  const {
    builderId,
    specId
  } = useParams<{
    builderId: string;
    specId: string;
  }>();
  const navigate = useNavigate();
  const spec = useMemo(() => mockSpecs.find(s => s.id === specId), [specId]);
  const model = useMemo(() => initialModels.find(m => m.id === spec?.modelId), [spec]);
  if (!spec || !model) {
    return <div className="p-12 text-center" data-id="element-7266">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-7267">Spec not found</h2>
        <Button className="mt-4" onClick={() => navigate(`/${builderId}/specs`)} data-id="element-7268">
          Back to Specs
        </Button>
      </div>;
  }
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  // Group selections by category
  const groupedSelections = useMemo(() => {
    const groups: Record<string, typeof spec.selections> = {};
    spec.selections.forEach(sel => {
      if (!groups[sel.category]) groups[sel.category] = [];
      groups[sel.category].push(sel);
    });
    return groups;
  }, [spec]);
  return <div className="space-y-6 max-w-6xl mx-auto pb-12" data-id="element-7269">
      {/* Header */}
      <div className="flex items-center gap-4 mb-2" data-id="element-7270">
        <Button variant="ghost" onClick={() => navigate(`/${builderId}/specs`)} className="text-slate-500 hover:text-slate-900 px-2" data-id="element-7271">
          <ArrowLeft className="h-5 w-5" data-id="element-7272" />
        </Button>
        <div className="flex-1" data-id="element-7273">
          <div className="flex items-center gap-3" data-id="element-7274">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-7275">{spec.name}</h1>
            <Badge variant={spec.status === 'published' ? 'success' : 'neutral'} data-id="element-7276">
              {spec.status === 'published' ? 'Published' : 'Draft'}
            </Badge>
            {spec.stagingStyleId && <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium border border-slate-200" data-id="element-7277">
                <Sofa className="h-3 w-3" data-id="element-7278" />
                {stagingStyles.find(s => s.id === spec.stagingStyleId)?.name}
              </div>}
          </div>
          <p className="text-slate-500 text-sm mt-1" data-id="element-7279">
            Configuration for {spec.modelName}
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-7280">
          <Button variant="outline" leftIcon={<Edit className="h-4 w-4" data-id="element-7282" />} onClick={() => navigate(`/${builderId}/specs/${specId}/edit`)} data-id="element-7281">
            Edit Spec
          </Button>
          <Button leftIcon={<Play className="h-4 w-4" fill="currentColor" data-id="element-7284" />} data-id="element-7283">
            Launch Preview
          </Button>
          <Button variant="ghost" className="px-2" data-id="element-7285">
            <MoreVertical className="h-5 w-5 text-slate-400" data-id="element-7286" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-id="element-7287">
        {/* Left Column: Model Info */}
        <div className="lg:col-span-1 space-y-6" data-id="element-7288">
          <Card className="overflow-hidden" data-id="element-7289">
            <div className="h-48 bg-slate-100 relative" data-id="element-7290">
              {model.hasImage && model.imageUrl ? <img src={model.imageUrl} alt={model.name} className="w-full h-full object-cover" data-id="element-7291" /> : <div className="w-full h-full flex items-center justify-center text-slate-400" data-id="element-7292">
                  <ImageOff className="h-8 w-8" data-id="element-7293" />
                </div>}
            </div>
            <div className="p-5" data-id="element-7294">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-7295">{model.name}</h3>
              <p className="text-sm text-slate-500 mb-4" data-id="element-7296">{model.type}</p>

              <div className="grid grid-cols-2 gap-y-3 gap-x-4 mb-4" data-id="element-7297">
                <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-7298">
                  <Ruler className="h-4 w-4 text-slate-400" data-id="element-7299" />
                  <span data-id="element-7300">{model.sqft} sqft</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-7301">
                  <BedDouble className="h-4 w-4 text-slate-400" data-id="element-7302" />
                  <span data-id="element-7303">{model.beds} Beds</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-7304">
                  <Bath className="h-4 w-4 text-slate-400" data-id="element-7305" />
                  <span data-id="element-7306">{model.baths} Baths</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-7307">
                  <Layers className="h-4 w-4 text-slate-400" data-id="element-7308" />
                  <span data-id="element-7309">{model.stories} Story</span>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-between items-center" data-id="element-7310">
                <span className="text-sm font-medium text-slate-500" data-id="element-7311">
                  Base Price
                </span>
                <span className="text-lg font-bold text-slate-900" data-id="element-7312">
                  {formatCurrency(model.basePrice)}
                </span>
              </div>
            </div>
          </Card>

          <Card className="p-5" data-id="element-7313">
            <h3 className="font-semibold text-slate-900 mb-2" data-id="element-7314">Spec Details</h3>
            <p className="text-sm text-slate-600 mb-4" data-id="element-7315">{spec.description}</p>
            <div className="space-y-2 text-sm" data-id="element-7316">
              <div className="flex justify-between" data-id="element-7317">
                <span className="text-slate-500" data-id="element-7318">Created</span>
                <span className="text-slate-900 font-medium" data-id="element-7319">
                  {spec.createdAt}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-7320">
                <span className="text-slate-500" data-id="element-7321">Last Updated</span>
                <span className="text-slate-900 font-medium" data-id="element-7322">
                  {spec.updatedAt}
                </span>
              </div>
            </div>
          </Card>
        </div>

        {/* Right Column: Configuration */}
        <div className="lg:col-span-2 space-y-6" data-id="element-7323">
          <Card data-id="element-7324">
            <div className="p-5 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between rounded-t-xl" data-id="element-7325">
              <div data-id="element-7326">
                <h2 className="text-lg font-bold text-slate-900" data-id="element-7327">
                  Configuration
                </h2>
                <p className="text-sm text-slate-500" data-id="element-7328">
                  {spec.selections.length} options selected
                </p>
              </div>
              <div className="text-right" data-id="element-7329">
                <p className="text-sm font-medium text-slate-500" data-id="element-7330">
                  Total Upgrades
                </p>
                <p className="text-2xl font-bold text-primary-600" data-id="element-7331">
                  +{formatCurrency(spec.totalUpgradePrice)}
                </p>
              </div>
            </div>

            <div className="p-0" data-id="element-7332">
              {Object.entries(groupedSelections).map(([category, selections], idx, arr) => <div key={category} className={idx !== arr.length - 1 ? 'border-b border-slate-100' : ''} data-id="element-7333">
                    <div className="px-5 py-3 bg-slate-50/80 border-y border-slate-100 first:border-t-0" data-id="element-7334">
                      <h3 className="font-semibold text-slate-800" data-id="element-7335">
                        {category}
                      </h3>
                    </div>
                    <div className="divide-y divide-slate-50" data-id="element-7336">
                      {selections.map((sel, i) => <div key={i} className="p-5 flex items-center justify-between hover:bg-slate-50/50 transition-colors" data-id="element-7337">
                          <div data-id="element-7338">
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1" data-id="element-7339">
                              {sel.subcategory} • {sel.variantSet}
                            </p>
                            <p className="font-medium text-slate-900" data-id="element-7340">
                              {sel.optionName}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-7341">
                            {sel.price > 0 ? <span className="text-sm font-semibold text-slate-900" data-id="element-7342">
                                +{formatCurrency(sel.price)}
                              </span> : <span className="text-sm font-medium text-emerald-600" data-id="element-7343">
                                Included
                              </span>}
                          </div>
                        </div>)}
                    </div>
                  </div>)}
            </div>

            <div className="p-5 bg-slate-50 border-t border-slate-200 rounded-b-xl flex justify-between items-center" data-id="element-7344">
              <span className="font-semibold text-slate-900" data-id="element-7345">
                Total Configured Price
              </span>
              <span className="text-xl font-bold text-slate-900" data-id="element-7346">
                {formatCurrency(model.basePrice + spec.totalUpgradePrice)}
              </span>
            </div>
          </Card>

          {/* Marketing Assets Panel */}
          <Card className="overflow-hidden" data-id="element-7347">
            {spec.renderStatus === 'ready' && <>
                <div className="p-5 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between" data-id="element-7348">
                  <div data-id="element-7349">
                    <h2 className="text-lg font-bold text-slate-900" data-id="element-7350">
                      Marketing Assets
                    </h2>
                    <p className="text-sm text-slate-500" data-id="element-7351">
                      Generated {spec.marketingAssets.generatedAt}
                    </p>
                  </div>
                  <Badge variant="success" className="bg-emerald-100 text-emerald-700 border-emerald-200" data-id="element-7352">
                    Ready
                  </Badge>
                </div>
                <div className="divide-y divide-slate-100" data-id="element-7353">
                  <div className="p-5 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-7354">
                    <div className="flex items-center gap-3" data-id="element-7355">
                      <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-7356">
                        <ImageIcon className="h-5 w-5" data-id="element-7357" />
                      </div>
                      <div data-id="element-7358">
                        <p className="font-medium text-slate-900" data-id="element-7359">
                          Still Images
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-7360">
                          {spec.marketingAssets.stillImages} high-res images
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" leftIcon={<Download className="h-3 w-3" data-id="element-7362" />} data-id="element-7361">
                      Download All
                    </Button>
                  </div>
                  <div className="p-5 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-7363">
                    <div className="flex items-center gap-3" data-id="element-7364">
                      <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600" data-id="element-7365">
                        <Film className="h-5 w-5" data-id="element-7366" />
                      </div>
                      <div data-id="element-7367">
                        <p className="font-medium text-slate-900" data-id="element-7368">
                          Motion Assets
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-7369">
                          {spec.marketingAssets.motionAssets} videos & GIFs
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" leftIcon={<Download className="h-3 w-3" data-id="element-7371" />} data-id="element-7370">
                      Download All
                    </Button>
                  </div>
                  <div className="p-5 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-7372">
                    <div className="flex items-center gap-3" data-id="element-7373">
                      <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600" data-id="element-7374">
                        <Globe className="h-5 w-5" data-id="element-7375" />
                      </div>
                      <div data-id="element-7376">
                        <p className="font-medium text-slate-900" data-id="element-7377">
                          Interactive Tour
                        </p>
                        <p className="text-xs text-slate-500 truncate max-w-[200px]" data-id="element-7378">
                          {spec.marketingAssets.tourUrl}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" leftIcon={<Copy className="h-3 w-3" data-id="element-7380" />} data-id="element-7379">
                      Copy Link
                    </Button>
                  </div>
                </div>
                <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center" data-id="element-7381">
                  <button className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors" data-id="element-7382">
                    Regenerate Assets
                  </button>
                  <Button leftIcon={<Download className="h-4 w-4" data-id="element-7384" />} data-id="element-7383">
                    Download Full Package
                  </Button>
                </div>
              </>}

            {spec.renderStatus === 'rendering' && <>
                <div className="p-5 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between" data-id="element-7385">
                  <h2 className="text-lg font-bold text-slate-900" data-id="element-7386">
                    Marketing Assets
                  </h2>
                  <Badge variant="neutral" className="bg-amber-100 text-amber-700 border-amber-200 animate-pulse" data-id="element-7387">
                    Rendering
                  </Badge>
                </div>
                <div className="p-8 text-center" data-id="element-7388">
                  <div className="w-full bg-slate-100 rounded-full h-2 mb-6 overflow-hidden" data-id="element-7389">
                    <div className="bg-gradient-to-r from-amber-400 to-primary-500 h-2 rounded-full w-2/3 animate-pulse" data-id="element-7390"></div>
                  </div>
                  <Loader2 className="h-8 w-8 text-primary-500 animate-spin mx-auto mb-3" data-id="element-7391" />
                  <p className="font-medium text-slate-900" data-id="element-7392">
                    Your marketing package is being generated...
                  </p>
                  <p className="text-sm text-slate-500 mt-1" data-id="element-7393">
                    Estimated time: 5-10 minutes
                  </p>
                </div>
              </>}

            {spec.renderStatus === 'queued' && <>
                <div className="p-5 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between" data-id="element-7394">
                  <h2 className="text-lg font-bold text-slate-900" data-id="element-7395">
                    Marketing Assets
                  </h2>
                  <Badge variant="neutral" data-id="element-7396">Queued</Badge>
                </div>
                <div className="p-8 text-center" data-id="element-7397">
                  <Clock className="h-8 w-8 text-slate-400 mx-auto mb-3" data-id="element-7398" />
                  <p className="font-medium text-slate-900" data-id="element-7399">
                    This spec is in the render queue
                  </p>
                  <p className="text-sm text-slate-500 mt-1" data-id="element-7400">
                    Estimated wait: 15-20 minutes
                  </p>
                </div>
              </>}

            {spec.renderStatus === 'not-generated' && <>
                <div className="p-5 border-b border-slate-200 bg-slate-50/50" data-id="element-7401">
                  <h2 className="text-lg font-bold text-slate-900" data-id="element-7402">
                    Marketing Assets
                  </h2>
                </div>
                <div className="p-8 text-center flex flex-col items-center" data-id="element-7403">
                  <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mb-4" data-id="element-7404">
                    <Wand2 className="h-8 w-8 text-slate-400" data-id="element-7405" />
                  </div>
                  <p className="font-medium text-slate-900 mb-2" data-id="element-7406">
                    No marketing assets generated yet
                  </p>
                  <p className="text-sm text-slate-500 max-w-sm mx-auto mb-6" data-id="element-7407">
                    Generate a complete marketing package including high-res
                    images, motion assets, and an interactive tour URL.
                  </p>
                  <Button leftIcon={<Wand2 className="h-4 w-4" data-id="element-7409" />} disabled={!spec.stagingStyleId} data-id="element-7408">
                    Generate Marketing Package
                  </Button>
                  {!spec.stagingStyleId && <p className="text-xs text-amber-600 mt-3" data-id="element-7410">
                      Select a staging style first to generate assets.
                    </p>}
                </div>
              </>}
          </Card>
        </div>
      </div>
    </div>;
}